// /app/frontend/src/pages/compliance/breach/components/Step1InitialReport.jsx
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Textarea } from '../../../../components/ui/textarea';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { CalendarInput } from '../../../../components/ui/calendar-input';
import { FieldError } from '../../../../components/ui/field-error';
import { AlertTriangle, Shield, CheckCircle2, HelpCircle, FileText, ChevronDown, ChevronUp, Lock, Unlock } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import api from '../../../../services/api';

export default function Step1InitialReport({
  formData,
  updateField,
  entities,
  errors = {},
  onValidationChange,
  onCloseIncident,
  saving
}) {
  const [showDecisionAid, setShowDecisionAid] = React.useState(false);
  const [allowOverride, setAllowOverride] = React.useState(false);
  const [generatingRef, setGeneratingRef] = React.useState(false);

  // Fetch next breach reference when marking as breach
  React.useEffect(() => {
    if (formData.is_personal_data_breach === true && !formData.breach_reference) {
      setGeneratingRef(true);
      api.get('/breach-reports/next-reference')
        .then(res => {
          if (res.data?.breach_reference) {
            updateField('breach_reference', res.data.breach_reference);
          }
        })
        .catch(err => console.error('Failed to generate breach reference:', err))
        .finally(() => setGeneratingRef(false));
    }
  }, [formData.is_personal_data_breach]);

  // Real-time validation on blur
  const handleFieldBlur = React.useCallback((fieldName) => {
    if (onValidationChange) {
      const newErrors = { ...errors };
      // Clear error for this field if it now has a value
      if (fieldName === 'entity_id' && formData.entity_id) {
        delete newErrors.entity_id;
      } else if (fieldName === 'breach_reference' && formData.breach_reference) {
        delete newErrors.breach_reference;
      } else if (fieldName === 'title' && formData.title) {
        delete newErrors.title;
      } else if (fieldName === 'awareness_date' && formData.awareness_date) {
        delete newErrors.awareness_date;
      } else if (fieldName === 'what_happened' && formData.what_happened) {
        delete newErrors.what_happened;
      }
      onValidationChange(newErrors);
    }
  }, [errors, formData, onValidationChange]);
  const isDataBreach = formData.is_personal_data_breach;
  const isNotBreach = formData.is_personal_data_breach === false;
  const isUnknown = formData.is_personal_data_breach === null || formData.incident_or_breach === 'unknown';

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Initial Report</h2>
        <p className="text-muted-foreground mt-1">
          Capture essential information quickly. The notification deadline starts from the awareness date.
        </p>
      </div>

      {/* Entity field - shown FIRST (required for all paths) per BUG-02 */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            Entity Information
          </CardTitle>
          <CardDescription>
            Select the entity affected by this incident.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div>
            <Label>Entity <span className="text-destructive">*</span></Label>
            <Select value={formData.entity_id} onValueChange={v => {
              updateField('entity_id', v);
              handleFieldBlur('entity_id');
            }}>
              <SelectTrigger
                data-testid="entity-select"
                className={cn("mt-1.5", errors.entity_id && "border-destructive bg-destructive/5")}
              >
                <SelectValue placeholder="Select entity" />
              </SelectTrigger>
              <SelectContent>
                {entities.map(e => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.entity_name} {e.client_name ? `(${e.client_name})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.entity_id && <FieldError message={errors.entity_id} />}
          </div>
        </CardContent>
      </Card>

      {/* Primary Question: Is this a Personal Data Breach? */}
      <Card className={cn("border-2", errors.is_personal_data_breach && "border-destructive bg-destructive/5")}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-primary" />
            Incident Classification
          </CardTitle>
          <CardDescription>
            Is this a personal data breach under data protection laws?
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* YES - It's a breach */}
            <button
              type="button"
              data-testid="is-breach-yes"
              onClick={() => {
                updateField('is_personal_data_breach', true);
                updateField('incident_or_breach', 'breach');
              }}
              className={cn(
                "p-5 border-2 rounded-xl text-left transition-all relative",
                isDataBreach === true
                  ? "border-red-500 bg-red-50 dark:bg-red-950/30 ring-2 ring-red-500"
                  : "border-border hover:border-red-300 hover:bg-red-50/50 dark:hover:bg-red-950/20"
              )}
            >
              {isDataBreach === true && (
                <CheckCircle2 className="absolute top-3 right-3 h-5 w-5 text-red-500" />
              )}
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <div className="font-semibold text-lg">Yes</div>
              </div>
              <p className="text-sm text-muted-foreground">
                This is a confirmed personal data breach that may need to be reported.
              </p>
            </button>

            {/* NO - Not a breach */}
            <button
              type="button"
              data-testid="is-breach-no"
              onClick={() => {
                updateField('is_personal_data_breach', false);
                updateField('incident_or_breach', 'incident');
              }}
              className={cn(
                "p-5 border-2 rounded-xl text-left transition-all relative",
                isNotBreach
                  ? "border-green-500 bg-green-50 dark:bg-green-950/30 ring-2 ring-green-500"
                  : "border-border hover:border-green-300 hover:bg-green-50/50 dark:hover:bg-green-950/20"
              )}
            >
              {isNotBreach && (
                <CheckCircle2 className="absolute top-3 right-3 h-5 w-5 text-green-500" />
              )}
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="font-semibold text-lg">No</div>
              </div>
              <p className="text-sm text-muted-foreground">
                This is a security incident but does not involve personal data.
              </p>
            </button>

            {/* UNKNOWN - Not yet determined */}
            <button
              type="button"
              data-testid="is-breach-unknown"
              onClick={() => {
                updateField('is_personal_data_breach', null);
                updateField('incident_or_breach', 'unknown');
              }}
              className={cn(
                "p-5 border-2 rounded-xl text-left transition-all relative",
                isUnknown
                  ? "border-amber-500 bg-amber-50 dark:bg-amber-950/30 ring-2 ring-amber-500"
                  : "border-border hover:border-amber-300 hover:bg-amber-50/50 dark:hover:bg-amber-950/20"
              )}
            >
              {isUnknown && (
                <CheckCircle2 className="absolute top-3 right-3 h-5 w-5 text-amber-500" />
              )}
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <HelpCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="font-semibold text-lg">Unknown</div>
              </div>
              <p className="text-sm text-muted-foreground">
                Continue documenting. Your DPO will make the final determination.
              </p>
            </button>
          </div>

          {/* Collapsible decision aid */}
          <button
            type="button"
            onClick={() => setShowDecisionAid(!showDecisionAid)}
            className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium pt-2"
          >
            {showDecisionAid ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            Not sure? Use this guide
          </button>

          {showDecisionAid && (
            <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg space-y-3">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-300">Decision Aid: Is this a personal data breach?</p>
              <div className="space-y-3 text-sm text-amber-800 dark:text-amber-200">
                <div className="flex gap-3">
                  <div className="font-semibold">1.</div>
                  <div>
                    <p className="font-medium">Did the incident involve personal data?</p>
                    <p className="text-xs mt-1 opacity-75">Any information that identifies or could identify a living individual (names, email, account numbers, etc.)</p>
                    <p className="text-xs mt-1 font-medium text-amber-700 dark:text-amber-400">→ If Yes, continue to Question 2</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="font-semibold">2.</div>
                  <div>
                    <p className="font-medium">Was that personal data accessed, disclosed, lost, altered, or destroyed without authorisation?</p>
                    <p className="text-xs mt-1 font-medium text-amber-700 dark:text-amber-400">→ If Yes, continue to Question 3</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="font-semibold">3.</div>
                  <div>
                    <p className="font-medium">Could this incident cause harm to the individuals whose data was affected?</p>
                    <p className="text-xs mt-1 opacity-75">Examples: embarrassment, financial loss, discrimination, loss of control over their information</p>
                    <p className="text-xs mt-1 font-medium text-red-600 dark:text-red-400">→ If Yes: This is likely a personal data breach. Select "Yes" and notify your DPO immediately.</p>
                  </div>
                </div>
                <div className="flex gap-3 pt-2 border-t border-amber-200 dark:border-amber-700">
                  <div className="font-semibold">If No</div>
                  <div>
                    <p className="text-xs">to Question 1: This may be a general security incident rather than a personal data breach. You can select "No" to record it as a non-breach incident.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="pt-2">
            <Label className="text-sm">Classification Notes</Label>
            <Textarea 
              data-testid="incident-notes" 
              value={formData.incident_notes || ''}
              onChange={e => updateField('incident_notes', e.target.value)}
              placeholder="Add notes about why this is or is not a personal data breach..."
              className="mt-1.5" 
              rows={3} 
            />
          </div>
        </CardContent>
      </Card>

      {/* If NO - Show contextual note */}
      {isNotBreach && (
        <div className="p-4 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex gap-3">
            <Shield className="h-5 w-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
            <div className="text-sm text-green-700 dark:text-green-300">
              <p className="font-medium">Recording Non-Breach Incident</p>
              <p className="mt-1">This incident will be recorded in your internal log. Recording non-breach incidents is good practice and may be important if questions are raised later by regulators, auditors, or clients. Your DPO will be notified and can review the classification.</p>
            </div>
          </div>
        </div>
      )}

      {/* If UNKNOWN - Show contextual note */}
      {isUnknown && (
        <div className="p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex gap-3">
            <HelpCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-700 dark:text-amber-300">
              <p className="font-medium">Classification Pending</p>
              <p className="mt-1">Continue documenting what you know. Your DPO will review the information and make the final determination. Do not delay recording while waiting for certainty — the 72-hour notification clock starts from the point of awareness, not the point of confirmation.</p>
            </div>
          </div>
        </div>
      )}

      {/* If YES - Show Breach Details form */}
      {isDataBreach === true && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              Breach Details
            </CardTitle>
            <CardDescription>
              Enter the breach reference number and key details. The 72-hour notification deadline starts from the awareness date.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Breach Reference <span className="text-destructive">*</span></Label>
              <div className="flex items-center gap-2 mt-1.5">
                <Input
                  data-testid="breach-reference"
                  value={formData.breach_reference || ''}
                  onChange={e => updateField('breach_reference', e.target.value)}
                  onBlur={() => handleFieldBlur('breach_reference')}
                  placeholder="e.g., BR-2026-001"
                  disabled={!allowOverride && formData.breach_reference}
                  className={cn("font-mono flex-1", errors.breach_reference && "border-destructive bg-destructive/5")}
                />
                {formData.breach_reference && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setAllowOverride(!allowOverride)}
                    className="shrink-0"
                    title={allowOverride ? 'Lock reference' : 'Unlock to edit'}
                  >
                    {allowOverride ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                  </Button>
                )}
              </div>
              {generatingRef && (
                <p className="text-xs text-muted-foreground mt-1.5">Generating reference...</p>
              )}
              {!formData.breach_reference && !generatingRef && (
                <p className="text-xs text-muted-foreground mt-1.5">Auto-generated when you select "Yes" above.</p>
              )}
              {errors.breach_reference && <FieldError message={errors.breach_reference} />}
            </div>
            <div>
              <Label>Breach Title <span className="text-destructive">*</span></Label>
              <Input
                data-testid="breach-title"
                value={formData.title}
                onChange={e => updateField('title', e.target.value)}
                onBlur={() => handleFieldBlur('title')}
                placeholder="e.g., Email sent to wrong recipient"
                className={cn("mt-1.5", errors.title && "border-destructive bg-destructive/5")}
              />
              {errors.title && <FieldError message={errors.title} />}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Awareness Date <span className="text-destructive">*</span></Label>
                <CalendarInput
                  data-testid="awareness-date"
                  value={formData.awareness_date || ''}
                  onChange={val => {
                    updateField('awareness_date', val);
                    handleFieldBlur('awareness_date');
                  }}
                  defaultToday={true}
                  className={cn("mt-1.5", errors.awareness_date && "border-destructive bg-destructive/5")}
                  placeholder="DD/MM/YYYY"
                />
                {errors.awareness_date && <FieldError message={errors.awareness_date} />}
              </div>
              <div>
                <Label>Breach Date <span className="text-xs text-muted-foreground">(if known)</span></Label>
                <CalendarInput
                  data-testid="breach-date"
                  value={formData.breach_date || ''}
                  onChange={val => updateField('breach_date', val)}
                  className="mt-1.5"
                  placeholder="DD/MM/YYYY"
                />
              </div>
            </div>
            <div>
              <Label>What happened? <span className="text-destructive">*</span></Label>
              <Textarea
                data-testid="what-happened"
                value={formData.what_happened || ''}
                onChange={e => updateField('what_happened', e.target.value)}
                onBlur={() => handleFieldBlur('what_happened')}
                placeholder="Describe what happened and how it was discovered..."
                className={cn("mt-1.5", errors.what_happened && "border-destructive bg-destructive/5")}
                rows={5}
              />
              {errors.what_happened && <FieldError message={errors.what_happened} />}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
